/**
 * Markup only a rich-text editor produces: a closing tag for a known element, or a void element.
 *
 * Deliberately stricter than "contains something in angle brackets" so legacy plain-text answers
 * such as `weight <10 lbs` or `wound <approx 2cm> deep` are never mistaken for HTML. A loose test
 * costs real data here: the value gets rendered with `v-html` (the browser eats the fake tag) and
 * gets loaded into Quill on edit (Quill drops it, so saving truncates the answer).
 *
 * Must stay in sync with `RichTextHelper::HTML_MARKUP_PATTERN` (api
 * `src/app/Helpers/RichTextHelper.php`) — the back end makes the same call for the printed PDF, and
 * if the two disagree the same answer renders differently in the profile view and the print.
 */
const HTML_MARKUP_PATTERN =
  /<\/(?:p|div|span|strong|em|b|i|u|s|a|ul|ol|li|h[1-6]|table|tr|td|blockquote|pre)\s*>|<(?:br|hr|img)\b[^>]*\/?>/i;

/**
 * True when a string already carries HTML tags (Quill/TinyMCE bodies). Used to decide whether a
 * saved exam `textarea` should render/edit as rich text vs plain text.
 */
function looksLikeHtml(value: unknown): boolean {
  if (typeof value !== 'string') return false;

  const trimmed = value.trim();

  if (!trimmed) return false;

  return HTML_MARKUP_PATTERN.test(trimmed);
}

/** True when Quill/TinyMCE HTML has no visible text (empty shell, `<p><br></p>`, `&nbsp;`, etc.). */
function isRichTextEmpty(value: unknown): boolean {
  if (value == null) return true;

  const plainText = String(value)
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim();

  return !plainText;
}

/** Block tags whose closing tag ends a visual line — indentation after one is never real spacing. */
const BLOCK_CLOSE_PATTERN = /(<\/(?:p|div|h[1-6]|li|ul|ol|table|tbody|thead|tr|td|blockquote|pre)>)\s+</gi;

/** A paragraph holding nothing but breaks / spaces — already Quill's own blank line. */
const EMPTY_PARAGRAPH_PATTERN = /<p>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>/gi;

/**
 * Inner content of a block that cannot legally hold a `<p>`. The `<br>` rules below rewrite a break
 * into `</p><p>`, which is right inside a paragraph and wrong everywhere else: a Shift+Enter in a
 * TinyMCE list item is `<li>a<br>b</li>`, and rewriting it produced `<li>a</p><p>b</li>` — closing a
 * paragraph that was never opened. Same for a wrapped heading or a table cell.
 *
 * Only the INNER content is parked, not the tags: the repair rules further down reason about
 * `</h1>` and `</li>` boundaries, and hiding those would stop `<h1>Title</h1>Some text` from
 * getting its trailing paragraph.
 */
const UNPARAGRAPHABLE_BLOCK_PATTERN = /(<(li|td|th|h[1-6])\b[^>]*>)([\s\S]*?)(<\/\2>)/gi;

/**
 * Markers are delimited by NUL, which cannot survive HTML parsing and so cannot appear in a stored
 * note. A readable `[[AF_BLANK_LINE]]` collided with itself: a note that literally contained that
 * text had it split apart on restore.
 */
const MARKER_DELIMITER = '\u0000';

/**
 * TinyMCE note-template bodies often use `text<br>text`. Quill turns a single `<br>` into a
 * visual blank (or collapses loose breaks), so single breaks become consecutive non-empty `<p>`s
 * and runs of 2+ breaks become a real blank line.
 *
 * Runs on **load** in `AEditor` and `AFieldEditor`, so it sees Quill HTML far more often than
 * TinyMCE HTML — every note and exam answer already saved comes back through here. Anything it
 * mangles gets written back on the next save, so the Quill shapes below are the ones that matter:
 *
 * - `<p>a</p><p><br></p><p>b</p>` — a blank line, which must survive byte-for-byte
 * - `<strong>Weight</strong> <em>10 lbs</em>` — a space between two inline tags, which must survive
 */
function countTag(html: string, pattern: RegExp): number {
  return (html.match(pattern) || []).length;
}

function normalizeHtmlForQuill(html: string): string {
  const BLANK_LINE_MARKER = `${MARKER_DELIMITER}AF_BLANK_LINE${MARKER_DELIMITER}`;
  const EMPTY_PARAGRAPH_MARKER = `${MARKER_DELIMITER}AF_EMPTY_PARAGRAPH${MARKER_DELIMITER}`;
  const parkedBlocks: string[] = [];
  let value = String(html ?? '').trim();

  if (!value) return '';

  // Only between BLOCK tags. A bare `>\s+<` also ate the single space between two inline tags, so
  // `<strong>Weight</strong> <em>10 lbs</em>` came back as "Weight10 lbs".
  value = value.replace(BLOCK_CLOSE_PATTERN, '$1<');

  // Park the inside of every block that cannot hold a paragraph, so the `<br>` rules below never
  // split a list item, a table cell or a heading into mismatched tags. Restored last, verbatim.
  value = value.replace(UNPARAGRAPHABLE_BLOCK_PATTERN, (_match, openTag, _tagName, inner, closeTag) => {
    parkedBlocks.push(inner);

    return `${openTag}${MARKER_DELIMITER}AF_BLOCK_${parkedBlocks.length - 1}${MARKER_DELIMITER}${closeTag}`;
  });

  // Park a standalone empty paragraph whole. It is ALREADY the blank line we want, and the two
  // rules below would otherwise take it apart: `<br>` → `</p><p>` turns it into `<p></p><p></p>`,
  // and routing it through BLANK_LINE_MARKER emitted a stray `</p>` plus an unclosed `<p>`, because
  // that marker's replacement assumes the blank sits INSIDE a paragraph.
  value = value.replace(EMPTY_PARAGRAPH_PATTERN, EMPTY_PARAGRAPH_MARKER);

  value = value.replace(/(?:<br\s*\/?>\s*){2,}/gi, BLANK_LINE_MARKER);
  value = value.replace(/<br\s*\/?>/gi, '</p><p>');
  value = value.split(BLANK_LINE_MARKER).join('</p><p><br></p><p>');

  // Restore before the repair rules below — they reason about `<p>` / `</p>` boundaries and would
  // treat the bare marker text as paragraph content.
  value = value.split(EMPTY_PARAGRAPH_MARKER).join('<p><br></p>');

  value = value.replace(/(<\/(?:h[1-6]|div)>|^)\s*<\/p>/gi, '$1');
  value = value.replace(/(<\/(?:h[1-6]|p|div)>)\s*(?=[^<])/gi, '$1<p>');

  // Close what the `<br>` rules actually left open, by counting — not by looking at the last tag.
  // "Does it end in `</p>`" appended a stray `</p>` to any body that simply ended in a list or a
  // table, e.g. `<h1>Exam</h1><p>Intro</p><ul>…</ul>` came back with a dangling close.
  const needsOpeningParagraph = countTag(value, /<\/p>/g) > countTag(value, /<p\b[^>]*>/g);

  if (needsOpeningParagraph) {
    value = `<p>${value}`;
  }

  const needsClosingParagraph = countTag(value, /<p\b[^>]*>/g) > countTag(value, /<\/p>/g);

  if (needsClosingParagraph) {
    value = `${value}</p>`;
  }

  // `<br>` at the very start or end of a paragraph leaves a truly empty one behind.
  value = value.replace(/<p>\s*<\/p>/gi, '');

  // Last, so nothing above can touch the parked content — a `<br>` inside a list item is Quill's
  // own soft break and belongs exactly where the author put it.
  value = value.replace(
    new RegExp(`${MARKER_DELIMITER}AF_BLOCK_(\\d+)${MARKER_DELIMITER}`, 'g'),
    (_match, parkedIndex) => parkedBlocks[Number(parkedIndex)]
  );

  return value;
}

export { isRichTextEmpty, looksLikeHtml, normalizeHtmlForQuill };
